import { RaoStep, RaoChunk, RaoSourceContext } from '../services/nuclia-service';
import { ReasoningStep, ChatSource } from '../types';

export function extractTitleFromMarkdown(text: string): { title: string; detail: string } {
    const match = text.match(/\*\*([^*]+)\*\*/);
    if (match) {
        const title = match[1].trim();
        const detail = text
            .replace(/\*\*[^*]+\*\*/, '')
            .replace(/^[\s,\-–—:]+/, '')
            .trim();
        return { title, detail };
    }
    return { title: '', detail: text };
}

export function isInternetSource(context: RaoSourceContext): boolean {
    return context.agentId === 'internet-agent' ||
        context.source === 'google' ||
        context.agentId?.includes('internet') ||
        context.source?.includes('google');
}

export function isDatasetSource(context: RaoSourceContext): boolean {
    return context.source?.startsWith('nucliadb-') ||
        context.agentId?.includes('nucliadb') ||
        (!isInternetSource(context) && context.chunks.length > 0);
}

const stepModulesToExclude = [
    'add_step',
    'remi_validation',
    'summarize'
];

const stepTitlesToExclude = [
    'Preparing RAG'
];

export function mapRaoStepsToReasoningSteps(steps: RaoStep[]): ReasoningStep[] {
    let stepsToInclude = steps.filter(step => step.module);
    // Exclude those steps with all 3 title, value and reason missing
    const requiredFields: (keyof RaoStep)[] = ["title", "value", "reason"];

    stepsToInclude = stepsToInclude.filter((obj: RaoStep) =>
        requiredFields.every(field => {
            const value = obj[field];
            return (
                value !== undefined &&
                value !== null &&
                !(typeof value === "string" && value.trim() === "")
            );
        })
    );
    stepsToInclude = stepsToInclude.filter(step => !stepModulesToExclude.includes(step.module!));
    stepsToInclude = stepsToInclude.filter(step => !stepTitlesToExclude.includes(step.title!));

    return stepsToInclude
        .map(step => mapRaoStep(step));

    // Useful for debugging - maps entire step object as JSON
    // return steps
    //     .map(step => ({
    //         title: step.title || 'Untitled Step',
    //         reason: JSON.stringify(step, null, 2)
    //     }));
}

function mapRaoStep(step: RaoStep): ReasoningStep {
    const reasonParts: string[] = [];
    const titleParts: string[] = [];

    if (step.module) titleParts.push(`[${step.module}]`);
    if (step.title) titleParts.push(step.title);

    if (step.reason) reasonParts.push(`Reason: ${step.reason}`);
    if (step.value) reasonParts.push(`Value: ${step.value}`);

    return {
        title: titleParts.join(' - '),
        reason: reasonParts.join('\n') || ''
    }
}

export function mapSourceContextToSources(context: RaoSourceContext): ChatSource[] {
    const sources: ChatSource[] = [];
    const isInternet = isInternetSource(context);
    const sourceType = isInternet ? 'Internet Search' : 'Dataset';

    for (const chunk of context.chunks) {
        const { title, detail } = extractTitleFromMarkdown(chunk.text);

        let link = '#';
        if (isInternet) {
            if (chunk.url && chunk.url.length > 0) {
                link = chunk.url[0];
            }
        } else {
            if (chunk.originUrl) {
                link = chunk.originUrl;
            }
        }

        sources.push({
            title: title || chunk.title || context.title || 'Source',
            type: sourceType,
            text: detail || chunk.text,
            link,
            chunkId: chunk.chunkId,
            // For dataset sources, store the source ID for potential document fetch
            resourceId: !isInternet ? context.source : undefined,
        });
    }

    return sources;
}

/**
 * Map all RaoSourceContexts to ChatSource array
 */
export function mapAllSourceContextsToSources(contexts: RaoSourceContext[]): ChatSource[] {
    return contexts.flatMap(mapSourceContextToSources);
}

export function isInternetSearchStep(step: RaoStep): boolean {
    return step.agentPath?.includes('internet') ?? false;
}

export function extractSourcesFromSteps(steps: RaoStep[]): ChatSource[] {
    const sources: ChatSource[] = [];

    steps.forEach(step => {
        if (isInternetSearchStep(step) && step.value) {
            const match = step.value.match(/(\d+) results found/);
            if (match && step.reason) {
                sources.push({
                    title: `Internet Search - ${match[1]} results`,
                    type: 'Internet Search',
                    text: step.reason,
                    link: '#'
                });
            }
        }
    });

    return sources;
}

export function extractSummaryFromSteps(steps: RaoStep[]): string {
    const summarizeStep = steps.find(step => step.module === 'summarize');
    return summarizeStep?.value || '';
}

// Legacy function - kept for backward compatibility
export function mapChunksToSources(chunks: RaoChunk[]): ChatSource[] {
    if (chunks.length === 0) return [];

    return chunks.map((chunk, index) => {
        const { title, detail } = extractTitleFromMarkdown(chunk.text);
        return {
            title: title || chunk.title || `Source ${index + 1}`,
            type: chunk.source?.includes('internet') ? 'Internet Search' : 'Dataset',
            text: detail || chunk.text,
            link: chunk.url?.[0] || '#'
        };
    });
}

// Legacy function - kept for backward compatibility
export function combineAllSources(chunks: RaoChunk[], steps: RaoStep[]): ChatSource[] {
    const chunkSources = mapChunksToSources(chunks);
    const stepSources = extractSourcesFromSteps(steps);
    return [...chunkSources, ...stepSources];
}
